import { useRef, useCallback, useState, useEffect } from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl/mapbox';
import { motion, AnimatePresence } from 'framer-motion';
import { assets } from '../../data/events';
import { EventPopupContent } from '../Events/EventCard';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Swiss Romandie bounds
const INITIAL_VIEW = {
  latitude: 46.7,
  longitude: 6.65,
  zoom: 9,
  pitch: 0,
  bearing: 0
};

// Detect mobile
const isMobile = () => window.innerWidth < 768;

// Truck marker component
const TruckMarker = ({ event, isActive, onClick }) => {
  const size = isMobile() ? 'w-20' : 'w-28';
  const shadowSize = isMobile() ? 'w-14' : 'w-20';
  
  return (
    <motion.div
      data-testid="truck-marker"
      className="relative cursor-pointer"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onClick={onClick}
    >
      {/* Pulse ring behind truck */}
      {isActive && (
        <motion.div
          className="absolute inset-0 -m-6 rounded-full bg-[#97c7c3]/30"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.7, 0.2, 0.7]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      {/* Shadow */}
      <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 ${shadowSize} h-4 bg-black/20 rounded-full blur-sm`} />
      
      {/* Truck image */}
      <motion.img
        src={assets.truckImage}
        alt="L'Atelier by JS Food Truck - Suisse Romande"
        className={`${size} h-auto relative z-10`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
        style={{
          filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.35))'
        }}
      />
    </motion.div>
  );
};

// Event location marker with tooltip
const EventMarker = ({ event, isActive, isTruckHere, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (isTruckHere) return null;

  const isPast = event.status === 'past';
  const markerSize = isMobile() ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <div
      data-testid={`event-marker-${event.id}`}
      className="relative cursor-pointer"
      style={{ padding: '12px', margin: '-12px' }}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Outer ring for upcoming events */}
      {event.status === 'upcoming' && !isPast && (
        <motion.div
          className="absolute rounded-full border-2 border-[#97c7c3]"
          style={{ inset: '8px' }}
          animate={{ scale: [1, 1.4, 1], opacity: [1, 0.4, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
      
      {/* Main dot */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.3 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
        className={`${markerSize} rounded-full border-2 border-white shadow-lg transition-colors ${
          isPast 
            ? 'bg-gray-400' 
            : event.status === 'active'
              ? 'bg-emerald-500'
              : 'bg-[#97c7c3]'
        }`}
      />
      
      {/* Tooltip on hover - Desktop only */}
      <AnimatePresence>
        {showTooltip && !isMobile() && (
          <motion.div 
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-8 top-1/2 -translate-y-1/2 whitespace-nowrap z-50 pointer-events-none"
          >
            <div className="bg-white rounded-lg shadow-xl px-3 py-2 border border-gray-100">
              <p className="font-league text-sm uppercase tracking-wide text-black">
                {event.name}
              </p>
              <p className="font-dm text-xs text-gray-500 mt-0.5">
                {event.displayDates}
              </p>
              {isPast && (
                <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                  Terminé
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Location label - Always visible on desktop when no tooltip */}
      {!isMobile() && !showTooltip && (
        <div className="absolute left-8 top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none">
          <span className={`font-dm text-xs font-medium bg-white/90 px-2 py-1 rounded shadow-sm ${
            isPast ? 'text-gray-400' : 'text-black'
          }`}>
            {event.location}
          </span>
        </div>
      )}
    </div>
  );
};

export const FoodTruckMap = ({ 
  currentEvent, 
  filteredEvents,
  allEvents,
  onEventSelect,
  selectedLocation,
  onEventClick
}) => {
  const mapRef = useRef(null);
  const [popupEvent, setPopupEvent] = useState(null);
  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [truckPosition, setTruckPosition] = useState({
    latitude: currentEvent?.coordinates?.lat || INITIAL_VIEW.latitude,
    longitude: currentEvent?.coordinates?.lng || INITIAL_VIEW.longitude
  });

  // Animate truck to new position when event changes
  useEffect(() => {
    if (currentEvent && currentEvent.coordinates) {
      const targetLat = currentEvent.coordinates.lat;
      const targetLng = currentEvent.coordinates.lng;
      
      setTruckPosition({
        latitude: targetLat,
        longitude: targetLng
      });
      
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [targetLng, targetLat],
          zoom: isMobile() ? 10 : 11,
          duration: 1500,
          essential: true
        });
      }
    }
  }, [currentEvent]);

  // Fly to location when filter changes
  useEffect(() => {
    if (selectedLocation !== 'Tous' && filteredEvents.length > 0) {
      const firstEvent = filteredEvents[0];
      if (mapRef.current && firstEvent.coordinates) {
        mapRef.current.flyTo({
          center: [firstEvent.coordinates.lng, firstEvent.coordinates.lat],
          zoom: isMobile() ? 10 : 11,
          duration: 1000
        });
      }
    } else if (selectedLocation === 'Tous' && mapRef.current) {
      mapRef.current.flyTo({
        center: [INITIAL_VIEW.longitude, INITIAL_VIEW.latitude],
        zoom: INITIAL_VIEW.zoom,
        duration: 1000
      });
    }
  }, [selectedLocation, filteredEvents]);

  const handleMarkerClick = useCallback((event) => {
    if (isMobile() && onEventClick) {
      // On mobile, open modal instead of popup
      onEventClick(event);
    } else {
      setPopupEvent(event);
    }
    const eventIndex = allEvents.findIndex(e => e.id === event.id);
    if (eventIndex >= 0) {
      onEventSelect(eventIndex);
    }
  }, [onEventSelect, allEvents, onEventClick]);

  const eventsToShow = selectedLocation === 'Tous' 
    ? allEvents 
    : filteredEvents;

  return (
    <div data-testid="map-container" className="w-full h-full">
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        reuseMaps
      >
        <NavigationControl position="bottom-right" showCompass={false} />

        {/* Event markers */}
        {eventsToShow.map((event) => (
          <Marker
            key={`marker-${event.id}`}
            latitude={event.coordinates.lat}
            longitude={event.coordinates.lng}
            anchor="center"
          >
            <EventMarker
              event={event}
              isActive={currentEvent?.id === event.id}
              isTruckHere={currentEvent?.id === event.id}
              onClick={() => handleMarkerClick(event)}
            />
          </Marker>
        ))}

        {/* Food Truck marker */}
        <Marker
          latitude={truckPosition.latitude}
          longitude={truckPosition.longitude}
          anchor="bottom"
        >
          <TruckMarker
            event={currentEvent}
            isActive={true}
            onClick={() => currentEvent && handleMarkerClick(currentEvent)}
          />
        </Marker>

        {/* Popup - Desktop only */}
        <AnimatePresence>
          {popupEvent && !isMobile() && (
            <Popup
              latitude={popupEvent.coordinates.lat}
              longitude={popupEvent.coordinates.lng}
              anchor="bottom"
              offset={[0, -60]}
              closeOnClick={false}
              onClose={() => setPopupEvent(null)}
              className="animate-fade-in"
            >
              <EventPopupContent event={popupEvent} />
            </Popup>
          )}
        </AnimatePresence>
      </Map>
    </div>
  );
};
